// frontend/src/components/AdminDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminService, teamService, supabase } from '../services/supabaseClient';
import toast, { Toaster } from 'react-hot-toast';
import EventSwitcher from './EventSwitcher';
import FlagDisplay from './FlagDisplay';
import AdminHeader from './AdminHeader';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { formatRelativeTime } from '../utils/dateUtils';

const AdminDashboard = ({ eventCode }) => {
  const currentEventCode = eventCode || 'mjwc2025';
  const [eventDetails, setEventDetails] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, complete, incomplete
  const [isExporting, setIsExporting] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [csvPreviewData, setCsvPreviewData] = useState(null); // { rows: [], playerData: [], totalPlayers: int }

  const [showLinkModal, setShowLinkModal] = useState(false);
  const [selectedTeamLink, setSelectedTeamLink] = useState('');

  // Add Team Modal State
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamCode, setNewTeamCode] = useState('');
  const [duplicateWarning, setDuplicateWarning] = useState(null); // { existingTeam: string, userConfirmed: boolean }

  const navigate = useNavigate();

  // Fetch data on mount and when event changes

  const fetchTeamsData = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Get Event Details to get ID
      const { data: event, error: eventError } = await supabase
        .from('events')
        .select('*')
        .eq('code', currentEventCode)
        .single();

      if (eventError || !event) throw new Error('Event not found');
      setEventDetails(event);

      // 2. Fetch all configured teams
      const { data: allTeams, error: teamsError } = await adminService.getTeamsForEvent(event.id);
      if (teamsError) throw teamsError;

      // 3. Fetch upload stats
      const { data: statsData, error: statsError } = await teamService.getTeamsByEvent(currentEventCode);
      if (statsError) throw statsError;

      // 4. Merge Data
      const statsMap = {};
      (statsData || []).forEach(s => {
        statsMap[s.team] = s;
      });

      const mergedTeams = (allTeams || []).map(team => {
        const stat = statsMap[team.name] || { total: 0, completed: 0 };
        return {
          ...team,
          urlSafeName: encodeURIComponent(team.name),
          total: stat.total,
          uploaded: stat.completed,
          lastUpdated: stat.lastUpdated,
          percentage: stat.total > 0 ? Math.round((stat.completed / stat.total) * 100) : 0
        };
      });

      setTeams(mergedTeams);
    } catch (error) {
      console.error('Error fetching teams:', error);
      toast.error('Failed to load teams data: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [currentEventCode]);

  // Fetch data on mount and when event changes
  useEffect(() => {
    fetchTeamsData();
  }, [currentEventCode, fetchTeamsData]);

  // Normalize team name for comparison (lowercase, trim, collapse whitespace)
  const normalizeTeamName = (name) => {
    if (!name) return '';
    return name.toLowerCase().trim().replace(/\s+/g, ' ');
  };

  // Find existing team with similar name
  const findSimilarTeam = (inputName) => {
    const normalizedInput = normalizeTeamName(inputName);
    if (!normalizedInput) return null;

    return teams.find(team => {
      const normalizedExisting = normalizeTeamName(team.name);
      return normalizedExisting === normalizedInput;
    });
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!newTeamName || !newTeamCode) {
      toast.error('Please fill in all fields');
      return;
    }

    // Check for duplicate team name (if not already confirmed by user)
    if (!duplicateWarning?.userConfirmed) {
      const similarTeam = findSimilarTeam(newTeamName);
      if (similarTeam) {
        setDuplicateWarning({ existingTeam: similarTeam.name, userConfirmed: false });
        return; // Stop here, wait for user confirmation
      }
    }

    const { error } = await adminService.createTeam({
      eventId: eventDetails.id,
      name: newTeamName,
      accessCode: newTeamCode
    });

    if (error) {
      toast.error('Failed to create team: ' + error.message);
    } else {
      toast.success('Team created successfully!');
      setShowAddTeamModal(false);
      setNewTeamName('');
      setNewTeamCode('');
      setDuplicateWarning(null);
      fetchTeamsData();
    }
  };

  // Handle user confirming to proceed despite duplicate warning
  const handleConfirmDuplicate = () => {
    setDuplicateWarning({ ...duplicateWarning, userConfirmed: true });
  };

  // Handle user canceling due to duplicate
  const handleCancelDuplicate = () => {
    setDuplicateWarning(null);
  };

  const generateRandomCode = () => {
    // Generate standard 3 char prefix + 3 numbers
    const prefix = newTeamName ? newTeamName.substring(0, 3).toUpperCase() : 'TEA';
    const randomNum = Math.floor(100 + Math.random() * 900);
    setNewTeamCode(`${prefix}${randomNum}`);
  };

  // Generate and copy team link using the code from DB
  const copyTeamLink = (team, e) => {
    e.stopPropagation(); // Prevent navigation to team detail

    // Construct URL manually since we have the code directly
    const url = `${window.location.origin}/${currentEventCode}/${team.access_code}`;

    if (url) {
      navigator.clipboard.writeText(url).then(() => {
        toast.success(`Link copied for ${team.name}!`);
        setSelectedTeamLink(url);
        setShowLinkModal(true);
      }).catch(() => {
        // Fallback for browsers that don't support clipboard API
        setSelectedTeamLink(url);
        setShowLinkModal(true);
      });
    }
  };

  const handleDeleteTeam = async (team, e) => {
    e.stopPropagation();

    // Safety Check: Require user to type the team name
    const userInput = window.prompt(
      `WARNING: This is a destructive action.\n\nAre you sure you want to delete the team "${team.name}"?\n\nPlease type "${team.name}" below to confirm:`
    );

    if (userInput !== team.name) {
      if (userInput !== null) { // User didn't click Cancel
        toast.error('Team name did not match. Deletion cancelled.');
      }
      return;
    }

    const { error } = await adminService.deleteTeam(team.id);
    if (error) {
      toast.error('Failed to delete team: ' + error.message);
    } else {
      toast.success('Team deleted successfully!');
      fetchTeamsData();
    }
  };

  // Filter teams based on search and status
  const filteredTeams = teams.filter(team => {
    const matchesSearch = team.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter =
      filterStatus === 'all' ||
      (filterStatus === 'complete' && team.percentage === 100) ||
      (filterStatus === 'incomplete' && team.percentage < 100);
    return matchesSearch && matchesFilter;
  });

  // Calculate global stats
  const globalStats = teams.reduce(
    (acc, team) => ({
      totalPlayers: acc.totalPlayers + team.total,
      uploadedPhotos: acc.uploadedPhotos + team.uploaded,
      completeTeams: acc.completeTeams + (team.percentage === 100 ? 1 : 0)
    }),
    { totalPlayers: 0, uploadedPhotos: 0, completeTeams: 0 }
  );

  // Derive most recently active team (for the activity ribbon)
  const mostRecentTeam = teams.reduce((latest, team) => {
    if (!team.lastUpdated) return latest;
    if (!latest || new Date(team.lastUpdated) > new Date(latest.lastUpdated)) {
      return team;
    }
    return latest;
  }, null);

  const globalPercentage = globalStats.totalPlayers > 0
    ? Math.round((globalStats.uploadedPhotos / globalStats.totalPlayers) * 100)
    : 0;

  // Export summary as CSV
  const exportSummary = () => {
    const csv = [
      ['Team', 'Access Code', 'Total Players', 'Photos Uploaded', 'Percentage', 'Status', 'Access Link'],
      ...teams.map(team => [
        team.name,
        team.access_code,
        team.total,
        team.uploaded,
        `${team.percentage}%`,
        team.percentage === 100 ? 'Complete' : 'Incomplete',
        `${window.location.origin}/${currentEventCode}/${team.access_code}`
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentEventCode}_upload_summary_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Summary exported with access links!');
  };

  // Step 1: Preview before export — fetches data and builds CSV rows, then shows preview modal.
  // When confirmedOnly is true, restrict to confirmed players.
  const previewAccreditation = async (confirmedOnly = false) => {
    if (!eventDetails?.short_name) {
      toast.error('Event data not loaded');
      return;
    }

    setIsExporting(true);
    const toastId = toast.loading('Preparing preview...');

    try {
      const { data: allPlayers, error } = await supabase
        .from('event_uploads')
        .select('*')
        .eq('event_name', eventDetails.short_name);

      if (error) throw error;

      const players = confirmedOnly ? (allPlayers || []).filter(p => p.confirmed) : (allPlayers || []);

      if (!players || players.length === 0) {
        toast.error(confirmedOnly ? 'No confirmed players for this event' : 'No players found for this event', { id: toastId });
        setIsExporting(false);
        return;
      }

      function teamDataMap(teamName) { return teamName; }
      const stripAccents = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');

      const csvRows = [['Group', 'Full Name', 'First Name', 'Last Name', 'Email', 'Country', 'Photos']];
      players.forEach(player => {
        const rawName = player.player_name || '';
        const normalizedName = stripAccents(rawName);
        const email = `${normalizedName.toLowerCase().replace(/[^a-z0-9]/g, '')}@example.com`;
        const photoFilename = player.photo_url ? `${normalizedName}.jpg` : '';
        csvRows.push([
          'Participating Team',
          normalizedName,
          stripAccents(player.first_name),
          stripAccents(player.last_name),
          email,
          teamDataMap(player.team),
          photoFilename
        ]);
      });

      setCsvPreviewData({ rows: csvRows, playerData: players, confirmedOnly });
      toast.dismiss(toastId);
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to load preview', { id: toastId });
    } finally {
      setIsExporting(false);
    }
  };

  // Step 2: Confirmed export — receives pre-built data and does the ZIP download
  const confirmExportAccreditation = async () => {
    if (!csvPreviewData) return;
    const { playerData, confirmedOnly } = csvPreviewData;

    setIsExporting(true);
    const toastId = toast.loading('Building ZIP...');

    try {
      const zip = new JSZip();
      const photosFolder = zip.folder('Photos');
      function teamDataMap(teamName) { return teamName; }
      const stripAccents = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');

      const csvRows = [['Group', 'Full Name', 'First Name', 'Last Name', 'Email', 'Country', 'Photos']];
      let successPhotoCount = 0;

      // Build CSV rows in order and fetch photos in parallel
      playerData.forEach((player) => {
        const rawName = player.player_name || '';
        const normalizedName = stripAccents(rawName);
        const email = `${normalizedName.toLowerCase().replace(/[^a-z0-9]/g, '')}@example.com`;
        const photoFilename = player.photo_url ? `${normalizedName}.jpg` : '';

        csvRows.push([
          'Participating Team',
          `"${normalizedName}"`,
          `"${stripAccents(player.first_name)}"`,
          `"${stripAccents(player.last_name)}"`,
          email,
          `"${teamDataMap(player.team)}"`,
          `"${photoFilename}"`
        ]);
      });

      // Fetch photos in parallel without affecting CSV row order
      const promises = playerData.map(async (player) => {
        if (player.photo_url) {
          try {
            const response = await fetch(player.photo_url);
            const blob = await response.blob();
            const rawName = player.player_name || '';
            const normalizedName = rawName.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
            const photoFilename = `${normalizedName}.jpg`;
            photosFolder.file(photoFilename, blob);
            successPhotoCount++;
          } catch (err) {
            console.error(`Failed to download photo for ${player.player_name}:`, err);
          }
        }
      });

      await Promise.all(promises);

      const BOM = '\uFEFF';
      const csvContent = BOM + csvRows.map(row => row.join(',')).join('\n');
      zip.file(`${currentEventCode}_${confirmedOnly ? 'confirmed_' : ''}accreditation.csv`, csvContent);

      toast.loading(`Zipping ${successPhotoCount} photos...`, { id: toastId });
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `${currentEventCode}_${confirmedOnly ? 'confirmed_' : ''}accreditation_${new Date().toISOString().split('T')[0]}.zip`);

      toast.success('Accreditation data exported!', { id: toastId });
      setCsvPreviewData(null);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export accreditation data', { id: toastId });
    } finally {
      setIsExporting(false);
    }
  };

  // Download a ZIP of photos for the whole event (grouped by team folder).
  // When confirmedOnly is true, restrict to confirmed players.
  const downloadEventPhotos = async (confirmedOnly = false) => {
    if (!eventDetails?.short_name) {
      toast.error('Event data not loaded');
      return;
    }
    setIsExporting(true);
    const toastId = toast.loading('Preparing photos...');
    try {
      const { data: allPlayers, error } = await supabase
        .from('event_uploads')
        .select('*')
        .eq('event_name', eventDetails.short_name);
      if (error) throw error;

      const withPhotos = (allPlayers || []).filter(p => p.photo_url && (!confirmedOnly || p.confirmed));
      if (withPhotos.length === 0) {
        toast.error(confirmedOnly ? 'No confirmed players with photos' : 'No photos to download', { id: toastId });
        setIsExporting(false);
        return;
      }

      const zip = new JSZip();
      const stripAccents = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '');
      let successCount = 0;
      const promises = withPhotos.map(async (player) => {
        try {
          const response = await fetch(player.photo_url);
          const blob = await response.blob();
          const teamFolder = zip.folder(player.team || 'Unknown');
          teamFolder.file(`${stripAccents(player.player_name)}.jpg`, blob);
          successCount++;
        } catch (err) {
          console.error(`Failed to download photo for ${player.player_name}:`, err);
        }
      });
      await Promise.all(promises);

      toast.loading(`Zipping ${successCount} photos...`, { id: toastId });
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `${currentEventCode}_${confirmedOnly ? 'confirmed_' : ''}photos_${new Date().toISOString().split('T')[0]}.zip`);
      toast.success(`Downloaded ${successCount} photos!`, { id: toastId });
    } catch (error) {
      console.error('Event photos export error:', error);
      toast.error('Failed to export photos', { id: toastId });
    } finally {
      setIsExporting(false);
    }
  };

  // Download bulk roster template
  const downloadBulkTemplate = () => {
    const csvContent = `team_name,player_name,first_name,last_name\nAustralia,Example Player 1,Example,Player One\nAustralia,Example Player 2,Example,Player Two\nGermany,Example Player 1,Example,Player One\nGermany,Example Player 2,Example,Player Two`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${currentEventCode}_roster_template.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Template downloaded!');
  };

  // Handle bulk roster import
  const handleBulkImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!eventDetails?.id || !eventDetails?.short_name) {
      toast.error('Event data not loaded');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target.result;
      const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');

      // Split one CSV line into trimmed fields, honoring simple double-quoted values.
      const splitCsvLine = (line) => {
        const out = [];
        let cur = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          const ch = line[i];
          if (inQuotes) {
            if (ch === '"') {
              if (line[i + 1] === '"') { cur += '"'; i++; } else { inQuotes = false; }
            } else { cur += ch; }
          } else if (ch === '"') {
            inQuotes = true;
          } else if (ch === ',') {
            out.push(cur); cur = '';
          } else {
            cur += ch;
          }
        }
        out.push(cur);
        return out.map(s => s.trim());
      };

      const teamKeys = ['team_name', 'team name', 'team'];
      const nameKeys = ['player_name', 'player name', 'name', 'full name'];
      const firstKeys = ['first_name', 'first name', 'first'];
      const lastKeys = ['last_name', 'last name', 'last'];

      // Detect + map a header row. Default to team,player when absent.
      const headerCols = lines.length > 0 ? splitCsvLine(lines[0]).map(c => c.toLowerCase()) : [];
      const hasHeader = headerCols.some(c => [...teamKeys, ...nameKeys, ...firstKeys, ...lastKeys].includes(c));

      let teamIdx = 0, nameIdx = 1, firstIdx = -1, lastIdx = -1;
      let dataLines = lines;
      if (hasHeader) {
        const foundTeam = headerCols.findIndex(c => teamKeys.includes(c));
        const foundName = headerCols.findIndex(c => nameKeys.includes(c));
        teamIdx = foundTeam === -1 ? 0 : foundTeam;
        nameIdx = foundName === -1 ? 1 : foundName;
        firstIdx = headerCols.findIndex(c => firstKeys.includes(c));
        lastIdx = headerCols.findIndex(c => lastKeys.includes(c));
        dataLines = lines.slice(1);
      }

      // Parse CSV
      const rows = [];
      dataLines.forEach(line => {
        const cols = splitCsvLine(line);
        const teamName = (cols[teamIdx] || '').trim();
        const first = firstIdx >= 0 ? (cols[firstIdx] || '').trim() : '';
        const last = lastIdx >= 0 ? (cols[lastIdx] || '').trim() : '';
        let playerName = (cols[nameIdx] || '').trim();
        if (!playerName) playerName = [first, last].filter(Boolean).join(' ').trim();

        if (teamName && playerName) {
          rows.push({
            team_name: teamName,
            player_name: playerName,
            first_name: first || null,
            last_name: last || null
          });
        }
      });

      if (rows.length === 0) {
        toast.error('No valid data found in CSV');
        return;
      }

      const { stats, error } = await adminService.bulkImportRoster(
        eventDetails.id,
        eventDetails.short_name,
        rows
      );

      if (error) {
        toast.error('Import failed: ' + error.message);
      } else {
        const msg = `Created ${stats.teamsCreated} teams, ${stats.playersCreated} players. Skipped ${stats.teamsSkipped} duplicate teams, ${stats.playersSkipped} duplicate players.`;
        toast.success(msg, { duration: 5000 });
        fetchTeamsData();
      }
    };
    reader.readAsText(file);
    event.target.value = null;
  };

  // Replaces the old render if (!isAuthenticated) ...
  // Since we are protected by route, we just render dashboard

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-center" />

      {/* Link Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Team Access Link</h3>
            <div className="bg-gray-100 p-3 rounded-lg mb-4 break-all">
              <code className="text-sm">{selectedTeamLink}</code>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(selectedTeamLink);
                  toast.success('Link copied!');
                }}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Copy Link
              </button>
              <button
                onClick={() => setShowLinkModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Team Modal */}
      {showAddTeamModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">Add New Team</h3>

            {/* Duplicate Warning */}
            {duplicateWarning && !duplicateWarning.userConfirmed && (
              <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-yellow-800">
                      A team with a similar name already exists:
                    </p>
                    <p className="text-sm text-yellow-700 font-semibold mt-1">
                      "{duplicateWarning.existingTeam}"
                    </p>
                    <p className="text-xs text-yellow-600 mt-2">
                      Do you still want to create "{newTeamName}"?
                    </p>
                    <div className="flex gap-2 mt-3">
                      <button
                        type="button"
                        onClick={handleCancelDuplicate}
                        className="px-3 py-1.5 text-xs bg-white border border-yellow-300 text-yellow-800 rounded hover:bg-yellow-100"
                      >
                        No, Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleConfirmDuplicate}
                        className="px-3 py-1.5 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700"
                      >
                        Yes, Create Anyway
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleCreateTeam}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Team Name</label>
                <input
                  type="text"
                  required
                  value={newTeamName}
                  onChange={(e) => {
                    setNewTeamName(e.target.value);
                    setDuplicateWarning(null); // Clear warning when user types
                  }}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-mjwc-primary"
                  placeholder="e.g. France"
                />
              </div>
              <div className="mb-6">
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-gray-700">Access Code</label>
                  <button
                    type="button"
                    onClick={generateRandomCode}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Generate Random
                  </button>
                </div>
                <input
                  type="text"
                  required
                  value={newTeamCode}
                  onChange={(e) => setNewTeamCode(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-mjwc-primary uppercase"
                  placeholder="e.g. FRA123"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddTeamModal(false);
                    setDuplicateWarning(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-mjwc-primary text-white rounded-lg hover:bg-mjwc-secondary"
                >
                  {duplicateWarning?.userConfirmed ? 'Create Anyway' : 'Create Team'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Header */}
      <AdminHeader currentPage="dashboard" />

      {/* Modern Hero Header - Side-Aligned Admin Version */}
      <div className="relative overflow-hidden bg-mjwc-primary">
        {/* Background Gradient & Pattern */}
        <div className="absolute inset-0 z-0 bg-gradient-to-br from-mjwc-primary via-mjwc-secondary to-indigo-900" />
        <div className="absolute inset-0 z-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]" />

        {/* Content Container */}
        <div className="relative z-20 px-6 py-10 sm:py-12 max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12">

            {/* Left Column: Event Identity & Stats */}
            <div className="flex-1 text-center lg:text-left">
              {/* Breadcrumb Row */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 mb-6">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/80">
                  <button
                    onClick={() => navigate('/admin/events')}
                    className="text-xs font-black uppercase tracking-widest hover:text-white transition-colors"
                  >
                    Events
                  </button>
                  <svg className="w-3 h-3 opacity-40" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-xs font-black uppercase tracking-widest text-white">
                    {eventDetails?.short_name || currentEventCode}
                  </span>
                </div>
              </div>

              {/* Event Title & Location */}
              <div className="mb-8">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-none mb-4 drop-shadow-2xl">
                  {eventDetails?.name || 'Event Dashboard'}
                </h1>

                <div className="flex flex-wrap justify-center lg:justify-start gap-3 text-white/70 text-sm font-bold">
                  {eventDetails?.location && (
                    <span className="flex items-center gap-1.5">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {eventDetails.location}
                    </span>
                  )}
                  {(eventDetails?.start_date || eventDetails?.end_date) && (
                    <span className="flex items-center gap-1.5">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {eventDetails.start_date && new Date(eventDetails.start_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                      {eventDetails.start_date && eventDetails.end_date && ' - '}
                      {eventDetails.end_date && new Date(eventDetails.end_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  )}
                </div>
              </div>

              {/* Glassmorphism Stats Card */}
              <div className="max-w-xl mx-auto lg:mx-0 bg-white/10 backdrop-blur-2xl rounded-3xl border border-white/20 p-6 shadow-2xl">
                {/* Global Progress */}
                <div className="mb-6">
                  <div className="flex justify-between items-end mb-2.5">
                    <span className="text-white text-xs font-black uppercase tracking-widest opacity-70">Overall Event Progress</span>
                    <span className="text-white text-xl font-black">{globalPercentage}%</span>
                  </div>
                  <div className="h-3 bg-white/5 rounded-full overflow-hidden border border-white/10 p-[2px]">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-400 via-emerald-500 to-cyan-500 rounded-full shadow-[0_0_15px_rgba(52,211,153,0.6)] transition-all duration-1000 ease-out"
                      style={{ width: `${globalPercentage}%` }}
                    />
                  </div>
                </div>

                {/* Dashboard Quick Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: 'Teams', value: teams.length, color: 'text-blue-400' },
                    { label: 'Complete', value: globalStats.completeTeams, color: 'text-emerald-400' },
                    { label: 'Photos', value: globalStats.uploadedPhotos, color: 'text-amber-400' },
                    { label: 'Players', value: globalStats.totalPlayers, color: 'text-purple-400' }
                  ].map((stat, i) => (
                    <div key={i} className="bg-white/5 rounded-2xl p-3 border border-white/5 flex flex-col items-center justify-center transition-all hover:bg-white/10 transform hover:-translate-y-1">
                      <span className={`text-2xl font-black leading-none mb-1 ${stat.color}`}>{stat.value}</span>
                      <span className="text-[11px] font-bold uppercase tracking-widest text-white/50">{stat.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column: Event Switcher & Visual */}
            <div className="flex-1 w-full max-w-sm lg:max-w-md">
              <div className="flex justify-center lg:justify-end mb-8 relative z-50">
                <div className="scale-90 lg:scale-100">
                  <EventSwitcher currentEventCode={currentEventCode} />
                </div>
              </div>

              {eventDetails?.thumbnail_url && (
                <div className="relative group perspective-1000 hidden md:block">
                  <div className="absolute -inset-4 bg-gradient-to-r from-blue-600 to-emerald-500 rounded-[2.5rem] opacity-30 blur-3xl group-hover:opacity-40 transition duration-1000" />

                  <div className="relative aspect-[4/3] overflow-hidden rounded-[2.5rem] border border-white/10 shadow-3xl transform -rotate-2 group-hover:rotate-0 transition-all duration-700">
                    <img
                      src={eventDetails.thumbnail_url}
                      alt={eventDetails.name}
                      className="w-full h-full object-cover transform scale-110 group-hover:scale-100 transition duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60" />
                    <div className="absolute bottom-6 left-8 right-8">
                      <span className="text-white/40 text-[9px] font-bold uppercase tracking-[0.4em] mb-2 block">Event Identity</span>
                      <div className="h-0.5 w-12 bg-emerald-500 rounded-full" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Dynamic Wave Transition */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none transform rotate-180 translate-y-[1px]">
          <svg className="relative block w-full h-[40px] lg:h-[60px] fill-gray-50" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"></path>
          </svg>
        </div>
      </div>

      {/* Recent activity ribbon */}
      {mostRecentTeam && (
        <div className="px-4 py-2 bg-emerald-50/60 border-b border-emerald-100">
          <div className="max-w-7xl mx-auto flex items-center gap-2 text-xs sm:text-sm">
            <span className="relative flex h-2 w-2 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span className="text-emerald-800 font-medium truncate">
              <span className="opacity-70">Last upload:</span>{' '}
              <span className="font-bold">{mostRecentTeam.team}</span>
              <span className="opacity-70"> · {formatRelativeTime(mostRecentTeam.lastUpdated)}</span>
            </span>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="px-4 py-4 bg-white border-b">
        <div className="space-y-3">
          {/* Search Bar */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search teams..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mjwc-primary"
            />
            <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* Filter and Export */}
          <div className="flex gap-2 flex-wrap items-center">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mjwc-primary"
            >
              <option value="all">All Teams</option>
              <option value="complete">Complete</option>
              <option value="incomplete">Incomplete</option>
            </select>
            <div className="relative">
              <button
                onClick={() => setShowExportDropdown(!showExportDropdown)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>

              {showExportDropdown && (
                <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200 overflow-hidden">
                  <button
                    onClick={() => {
                      exportSummary();
                      setShowExportDropdown(false);
                    }}
                    className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 border-b border-gray-100"
                  >
                    Summary (CSV)
                  </button>
                  <button
                    onClick={() => {
                      previewAccreditation();
                      setShowExportDropdown(false);
                    }}
                    disabled={isExporting}
                    className={`w-full text-left px-4 py-3 text-sm ${isExporting ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
                  >
                    {isExporting ? 'Exporting...' : 'Accreditation (CSV + Photos)'}
                  </button>

                  {/* Confirmed-only variants */}
                  <div className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-400 bg-gray-50 border-t border-gray-100">
                    Confirmed only
                  </div>
                  <button
                    onClick={() => {
                      downloadEventPhotos(true);
                      setShowExportDropdown(false);
                    }}
                    disabled={isExporting}
                    className={`w-full text-left px-4 py-3 text-sm border-b border-gray-100 ${isExporting ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
                  >
                    Confirmed Photos only (ZIP)
                  </button>
                  <button
                    onClick={() => {
                      previewAccreditation(true);
                      setShowExportDropdown(false);
                    }}
                    disabled={isExporting}
                    className={`w-full text-left px-4 py-3 text-sm ${isExporting ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
                  >
                    Confirmed Accreditation (CSV + Photos)
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={fetchTeamsData}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>

            {/* Jump to the event-wide list of players still missing a photo */}
            <button
              onClick={() => navigate(`/admin/${currentEventCode}/incomplete`)}
              className="px-4 py-2 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 transition-colors flex items-center gap-2"
              title="View all players missing a photo across teams"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Incomplete Players
            </button>

            {/* Bulk Import Controls */}
            <div className="flex gap-2 ml-auto">
              <button
                onClick={downloadBulkTemplate}
                className="px-4 py-2 border border-indigo-600 text-indigo-600 bg-white rounded-lg hover:bg-indigo-50 transition-colors flex items-center gap-2"
                title="Download roster CSV template"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Template
              </button>
              <label className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2 cursor-pointer">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Import Roster
                <input
                  type="file"
                  accept=".csv,.txt"
                  className="hidden"
                  onChange={handleBulkImport}
                />
              </label>

              {/* Add Team Button */}
              <button
                onClick={() => setShowAddTeamModal(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Team
              </button>
            </div>
          </div>
        </div>
      </div >

      {/* Teams List */}
      <div className="px-4 py-6">
        <div>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mjwc-primary"></div>
            </div>
          ) : filteredTeams.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No teams found matching your criteria
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTeams.map((team) => (
                <div
                  key={team.id || team.name}
                  className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all border border-gray-200 hover:border-mjwc-primary relative group"
                >
                  <button
                    onClick={() => navigate(`/admin/${currentEventCode}/${team.urlSafeName}`)}
                    className="w-full p-4 text-left"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <FlagDisplay teamName={team.name} size="md" />
                        <h3 className="font-semibold text-gray-900">{team.name}</h3>
                      </div>
                      {team.percentage === 100 && (
                        <div className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-semibold">
                          Complete
                        </div>
                      )}
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-2">
                      <div className="flex justify-between text-xs text-gray-600 mb-1">
                        <span>{team.uploaded}/{team.total} players</span>
                        <span className="font-semibold">{team.percentage}%</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all duration-500 ${team.percentage === 100
                            ? 'bg-green-500'
                            : team.percentage > 50
                              ? 'bg-yellow-500'
                              : 'bg-red-500'
                            }`}
                          style={{ width: `${team.percentage}%` }}
                        />
                      </div>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-gray-400 font-medium">
                        Code: {team.access_code}
                      </span>
                      {team.lastUpdated && (
                        <span className="flex items-center gap-1.5 text-mjwc-primary/60 font-bold bg-mjwc-primary/5 px-2 py-0.5 rounded-full">
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {formatRelativeTime(team.lastUpdated)}
                        </span>
                      )}
                      <svg className="w-5 h-5 text-gray-400 group-hover:text-mjwc-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </button>

                  {/* Action Buttons - positioned absolutely */}
                  <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 focus-within:opacity-100">
                    <button
                      onClick={(e) => copyTeamLink(team, e)}
                      className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                      title="Copy shareable link"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => handleDeleteTeam(team, e)}
                      className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                      title="Delete team"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* CSV Preview Modal */}
        {csvPreviewData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">
              <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Export Preview</h3>
                  <p className="text-sm text-gray-500 mt-1">Reviewing {csvPreviewData.playerData.length} records before zipping</p>
                </div>
                <button
                  onClick={() => setCsvPreviewData(null)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="flex-1 overflow-auto p-6">
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 mb-6 flex gap-3">
                  <svg className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-amber-800">
                    <strong>Normalization Active:</strong> All names have been stripped of accents (e.g., François → Francois) for system compatibility. Emails are auto-generated from these normalized names.
                  </p>
                </div>

                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50 border-b border-gray-200 text-gray-600 font-semibold">
                      <tr>
                        {csvPreviewData.rows[0].map((header, i) => (
                          <th key={i} className="px-4 py-3">{header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {csvPreviewData.rows.slice(1, 11).map((row, i) => (
                        <tr key={i} className="hover:bg-gray-50 transition-colors">
                          {row.map((cell, j) => (
                            <td key={j} className="px-4 py-3 text-gray-700">{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {csvPreviewData.rows.length > 11 && (
                  <p className="text-center text-gray-400 text-xs mt-4 font-medium italic">
                    Showing first 10 of {csvPreviewData.rows.length - 1} records...
                  </p>
                )}
              </div>

              <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 rounded-b-xl">
                <button
                  onClick={() => setCsvPreviewData(null)}
                  className="px-6 py-2.5 text-sm font-semibold text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmExportAccreditation}
                  disabled={isExporting}
                  className="px-8 py-2.5 bg-indigo-600 text-white text-sm font-bold rounded-lg hover:bg-indigo-700 shadow-lg shadow-indigo-200 transition-all transform hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isExporting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Confirm & Download ZIP
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div >
    </div >
  );
};

export default AdminDashboard;